"use client";

import React from "react";
import { ComponentConfig } from "@puckeditor/core";
import { businessData } from "@/data/business";

export interface ServiceItem {
  id: string;
  title: string;
  body: string;
  specs: string;
  icon?: string;
}

export interface ServicesGridProps {
  heading?: string;
  subheading?: string;
  services?: ServiceItem[];
}

export const ServicesGridBlock: ComponentConfig<ServicesGridProps> = {
  label: "Turnkey Services Grid",
  fields: {
    heading: { type: "text", label: "Section Heading" },
    subheading: { type: "textarea", label: "Section Subheading" },
    services: {
      type: "array",
      getItemSummary: (svc: any) => svc.title || "Service",
      arrayFields: {
        id: { type: "text", label: "ID" },
        title: { type: "text", label: "Service Title" },
        body: { type: "textarea", label: "Description" },
        specs: { type: "text", label: "Specs Badge Text" },
        icon: {
          type: "select",
          label: "Service Icon",
          options: [
            { label: "Factory", value: "factory" },
            { label: "Scale-up / gear", value: "gear" },
            { label: "Formulation lab", value: "flask" },
            { label: "Packaging", value: "box" },
            { label: "Quality shield", value: "shield" },
            { label: "Regulatory document", value: "doc" },
          ],
        },
      },
      defaultItemProps: {
        id: "new-capability",
        title: "New Manufacturing Capability",
        body: "Explain the scope, process, and commercial value of this capability for a private-label brand.",
        specs: "Define MOQ | Lead time | Testing or process detail",
        icon: "factory",
      },
    },
  },
  defaultProps: {
    heading: "Turnkey Cosmeceutical Manufacturing Pipeline",
    subheading: "End-to-end OEM/ODM capabilities spanning active bio-sourcing, vacuum homogenisation, stability testing, and custom packaging.",
    services: businessData.services,
  },
  render: ({ heading, subheading, services }) => {
    return (
      <section className="sec" id="services">
        <div className="container">
          <div className="sec-head">
            <h2>{heading}</h2>
            <p className="sec-sub">{subheading}</p>
          </div>
          <div className="svc-grid">
            {(services || []).map((svc, idx) => (
              <div className="svc-card" data-animate key={svc.id || idx}>
                <div>
                  <span className="svc-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {svc.icon === "gear" ? (
                        <>
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </>
                      ) : svc.icon === "flask" ? (
                        <>
                          <path d="M10 2v7.31L4.75 18a2 2 0 0 0 1.7 3h11.1a2 2 0 0 0 1.7-3L14 9.31V2z" />
                          <line x1="8.5" y1="2" x2="15.5" y2="2" />
                          <line x1="6" y1="14" x2="18" y2="14" />
                        </>
                      ) : svc.icon === "box" ? (
                        <>
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                          <line x1="12" y1="22.08" x2="12" y2="12" />
                        </>
                      ) : svc.icon === "shield" ? (
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      ) : svc.icon === "doc" ? (
                        <>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </>
                      ) : (
                        <path d="M2 20h20M4 20V10l4 3V10l4 3V4l8 4v12" />
                      )}
                    </svg>
                  </span>
                  <h3>{svc.title}</h3>
                  <p>{svc.body}</p>
                </div>
                <div className="svc-specs">{svc.specs}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  },
};
